import { useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash-es';
import type { TreeNode } from 'tree-model';

import { ID_ADD_SUBCLIP_TO_COLLECTION } from '../../../constants/reducer_ids';
import {
  getCollectionContents,
  getCollections,
} from '../../../ducks/collection_fetch/thunks';
import {
  clearTree,
  modifyBranchByNode,
  setTreeSearchLoading,
  setTreeSearchQuery,
} from '../../../ducks/tree/thunks';
import type {
  TreeReducerId,
  TreeReducerModel,
} from '../../../ducks/tree/types';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import LoadingBar from '../../../ui/loading_bar/loading_bar';
import SearchInput from '../../../ui/search_input/search_input';
import { selectUserRoles } from '../../../utils/get_user_roles';
import { UUID_ZERO } from '../../../utils/tree_handlers';
import { CollectionTreeRow } from '../../collection_tree_row/collection_tree_row';
import {
  searchCollectionTree,
  searchCollectionTreeDebounced,
} from '../../modals/add_to_collection_advanced/thunks';
import { getTreeNodePathKey } from '../../tree/get_tree_node_path_key';
import { Tree } from '../../tree/tree';

import styles from './subclip_collection_tree.module.css';

type SubclipCollectionTreeProps = {
  selectedCollectionId?: string;
  onSelectionChanged: (id?: string) => void;
};

export const SubclipCollectionTree = ({
  selectedCollectionId,
  onSelectionChanged,
}: SubclipCollectionTreeProps) => {
  const dispatch = useAppDispatch();
  const tree = useAppSelector(
    (state) => state.collectionsTreeAddSubclipToCollection.flatTree,
  );
  const roles = useAppSelector(selectUserRoles);
  const treeSearchQuery = useAppSelector(
    (state) => state.collectionsTreeAddSubclipToCollection.treeSearchQuery,
  );
  const treeSearchLoading = useAppSelector(
    (state) => state.collectionsTreeAddSubclipToCollection.treeSearchLoading,
  );

  const [initialSearchText, setInitialSearchText] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount
  useEffect(() => {
    if (roles.canReadCollections) {
      dispatch(setTreeSearchLoading(ID_ADD_SUBCLIP_TO_COLLECTION, true));
      dispatch(getCollections({}, ID_ADD_SUBCLIP_TO_COLLECTION)).finally(() => {
        dispatch(setTreeSearchLoading(ID_ADD_SUBCLIP_TO_COLLECTION, false));
      });
    }
    if (treeSearchQuery) {
      setInitialSearchText(treeSearchQuery);
    }
    return () => {
      dispatch(clearTree(ID_ADD_SUBCLIP_TO_COLLECTION));
    };
  }, []);

  const handleOnLoadMore = (node: TreeNode<TreeReducerModel>) =>
    dispatch(
      searchCollectionTreeDebounced(
        treeSearchQuery,
        ID_ADD_SUBCLIP_TO_COLLECTION,
        Number(node.model.page) + 1,
      ),
    );

  const handleOnExpand = (
    node: TreeNode<TreeReducerModel>,
    treeReducerId: TreeReducerId,
  ) => {
    if (!node.model.expanded && !node.children.length) {
      dispatch(getCollectionContents(node.model.id, {}, treeReducerId));
    }
    dispatch(
      modifyBranchByNode(treeReducerId, node, {
        expanded: !node.model.expanded,
      }),
    );

    // if the root element gets collapsed we refresh the tree
    if (node.model.id === UUID_ZERO && node.model.expanded) {
      dispatch(clearTree(treeReducerId));
      dispatch(getCollections({}, treeReducerId));
    }
  };

  const treeElements = tree.map((node) => {
    const isSearchResults = Boolean(treeSearchQuery);
    const shouldHaveBreadcrumbs =
      isSearchResults && node.parent?.model.id === UUID_ZERO;

    return (
      <CollectionTreeRow
        key={getTreeNodePathKey(node)}
        node={node}
        onLoadMore={handleOnLoadMore}
        onExpand={(item) => handleOnExpand(item, ID_ADD_SUBCLIP_TO_COLLECTION)}
        onClick={(item) => {
          const id = item.model.id === UUID_ZERO ? undefined : item.model.id;
          onSelectionChanged(id);
        }}
        selectedIds={selectedCollectionId ? [selectedCollectionId] : []}
        isSearchResults={isSearchResults}
        shouldHaveBreadcrumbs={shouldHaveBreadcrumbs}
      />
    );
  });

  const handleSearch = useCallback(
    debounce((searchTerm: string) => {
      dispatch(setTreeSearchLoading(ID_ADD_SUBCLIP_TO_COLLECTION, true));
      dispatch(setTreeSearchQuery(ID_ADD_SUBCLIP_TO_COLLECTION, searchTerm));
      dispatch(
        searchCollectionTree(searchTerm, ID_ADD_SUBCLIP_TO_COLLECTION),
      ).finally(() => {
        dispatch(setTreeSearchLoading(ID_ADD_SUBCLIP_TO_COLLECTION, false));
      });
    }, 400),
    [],
  );

  return (
    <div>
      <SearchInput
        className={styles.search_input}
        value={initialSearchText}
        onSearch={handleSearch}
        searchButton={false}
        canClear
        clearButtonClassName={styles.clear_button}
        inputClassName={styles.inner_search_input}
      />

      <div className={styles.tree_wrapper}>
        <div className={styles.tree}>
          {treeSearchLoading ? <LoadingBar /> : <Tree items={treeElements} />}
        </div>
      </div>
    </div>
  );
};
